import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { AuthShell } from '@/components/AuthShell'
import { Alert, PageSpinner } from '@/components/ui'
import { confirmVerification, errorMessage } from '@/lib/pb'

/**
 * Landing page for the link in the verification email.
 *
 * PocketBase's default email points at its own UI; CONFIGURE.md replaces that
 * template so it points here instead: {APP_URL}/verify?token={TOKEN}.
 */
export function Verify() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [state, setState] = useState<'working' | 'done' | 'failed'>('working')
  const [error, setError] = useState('')
  // StrictMode double-invokes effects in dev; a verification token is single-use,
  // so the second call would fail and show a spurious error.
  const attempted = useRef(false)

  useEffect(() => {
    if (attempted.current) return
    attempted.current = true

    if (!token) {
      setState('failed')
      setError('That link is missing its token. Try the link in your email again.')
      return
    }

    confirmVerification(token)
      .then(() => setState('done'))
      .catch((err: unknown) => {
        setState('failed')
        setError(errorMessage(err))
      })
  }, [token])

  if (state === 'working') {
    return (
      <AuthShell title="Confirming your email">
        <PageSpinner label="Just a moment" />
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title={state === 'done' ? 'Email confirmed' : 'That link did not work'}
      footer={
        <Link className="text-brand hover:underline" to="/login">
          Go to sign in
        </Link>
      }
    >
      {state === 'done' ? (
        <Alert tone="success">You can sign in now.</Alert>
      ) : (
        <Alert>
          {error} Verification links expire — request a fresh one from the sign-in page.
        </Alert>
      )}
    </AuthShell>
  )
}
